// The BE's `futureStops` replaces v2's future-daily fanout.
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'
import { dayOfWeekForServiceDate } from 'utils/routeStopOperations/v2/dayOfWeek'
import { getDailyOpsScope } from 'utils/routeStopOperations/v2/decisions'
import { makePreflightStep } from 'utils/routeStopOperations/v2/steps/preflight'
import { makeUnassignDailySlotStep } from 'utils/routeStopOperations/v2/steps/unassignDailySlot'
import { makeUnassignMasterSlotStep } from 'utils/routeStopOperations/v2/steps/unassignMasterSlot'
import { makeVerifyStep } from 'utils/routeStopOperations/v2/steps/verify'

import type { Deps, OperationPlan, PipelineStep, UnassignIntent } from 'types/modules/routing'

const STRATEGY_ID = 'v3-slot' as const

// A master stop carries no workOrderId, so only the daily rows can be verified.
const dailyWorkOrderIds = (intent: UnassignIntent): ReadonlyArray<string> =>
  intent.stops.filter(stop => isDailyStop(stop) || isOnRequestStop(stop)).map(stop => String(stop.workOrderId))

export const buildUnassignSlotPipeline = (intent: UnassignIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = [makePreflightStep({ intent }, deps) as PipelineStep]

  steps.push(
    makeUnassignDailySlotStep({ routeId: intent.routeId, serviceDate: intent.serviceDate, rows: intent.stops }, deps) as PipelineStep
  )

  const scope = getDailyOpsScope(intent)
  const dayOfWeek = scope.runMasterFirst ? dayOfWeekForServiceDate(intent.serviceDate) : null
  if (dayOfWeek) {
    steps.push(
      makeUnassignMasterSlotStep({ routeId: intent.routeId, dayOfWeek, rows: intent.stops, futureStops: true }, deps) as PipelineStep
    )
  }

  steps.push(makeVerifyStep({ intent, expectedUnassignedWorkOrderIds: dailyWorkOrderIds(intent) }, deps) as PipelineStep)

  return { strategy: STRATEGY_ID, intent, steps }
}
